import React from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import SelectInput from '../inputs/select';

export default function SyncInvoicesOverrides({
    selectedConnection,
    entityType,
    billTab,
    invoiceStatus,
    setInvoiceStatus,
    purchaseOrderStatus,
    setPurchaseOrderStatus,
    squashLineItems,
    setSquashLineItems,
    accountCode,
    setAccountCode,
    expenseAccountCode,
    setExpenseAccountCode,
    cogsAccountCode,
    setCogsAccountCode,
    contractorAccountCode,
    setContractorAccountCode,
    taxType,
    setTaxType,
    expenseTaxType,
    setExpenseTaxType,
    exchangeRate,
    setExchangeRate,
    accountsList,
    taxRatesList,
    invoiceStatusesList,
    poStatusesList,
    invoiceStatusItems,
    loadingMetadata,
    syncing
}) {
    if (!selectedConnection) return null;

    return (
        <Accordion className="mb-3" data-cy="sync-invoices-override-accordion">
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <span className="font-weight-bold text-dark small text-uppercase override-accordion-title">
                        ⚙️ Override Integration Settings (Optional)
                    </span>
                </Accordion.Header>
                <Accordion.Body className="p-3 bg-light">
                    {loadingMetadata ? (
                        <div className="py-3 text-center text-muted small">
                            <Spinner size="sm" animation="border" className="me-2" />
                            Loading connection accounts & tax rates...
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <SelectInput
                                        label={entityType === 'invoice' ? "Default Invoice(Progress Claim) Status" : "Default Bill(Vendor Invoices) Status"}
                                        value={entityType === 'invoice' ? invoiceStatus : purchaseOrderStatus}
                                        onChange={(e) => entityType === 'invoice' ? setInvoiceStatus(e.target.value) : setPurchaseOrderStatus(e.target.value)}
                                        disabled={syncing}
                                        dataCy="sync-invoices-override-status"
                                        items={((entityType === 'invoice' ? invoiceStatusesList : poStatusesList).length > 0
                                            ? (entityType === 'invoice' ? invoiceStatusesList : poStatusesList)
                                            : invoiceStatusItems
                                        ).map(opt => ({ value: opt.value, key: opt.label || opt.key || opt.value }))}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="align-with-inputs">
                                        <Form.Check
                                            type="checkbox"
                                            label={entityType === 'invoice' ? "Squash line items for Invoices (AR)" : "Squash line items for Bills (AP)"}
                                            checked={squashLineItems}
                                            onChange={(e) => setSquashLineItems(e.target.checked)}
                                            disabled={syncing}
                                            data-cy="sync-invoices-override-squash"
                                        />
                                    </Form.Group>
                                    <div className="mt-2 small">
                                        ℹ️ Line Amount Type defaults to <strong>Tax Exclusive</strong>.
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    {entityType === 'invoice' ? (
                                        <SelectInput
                                            label="Revenue Account"
                                            value={accountCode}
                                            onChange={(e) => setAccountCode(e.target.value)}
                                            disabled={syncing}
                                            dataCy="sync-invoices-override-account"
                                            items={accountsList.map(a => ({ value: a.code, key: `${a.code} - ${a.name}` }))}
                                        />
                                    ) : billTab === 'contractor' ? (
                                        <>
                                            <SelectInput
                                                label="Contractor Account (Subcontractors)"
                                                value={contractorAccountCode}
                                                onChange={(e) => setContractorAccountCode(e.target.value)}
                                                disabled={syncing}
                                                dataCy="sync-invoices-override-contractor-account"
                                                items={accountsList.map(a => ({ value: a.code, key: `${a.code} - ${a.name}` }))}
                                            />
                                            <span className="d-block mt-1 small italic-subtext">
                                                Used for Subcontractor bills.
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <SelectInput
                                                label="Supplier Account (COGS)"
                                                value={cogsAccountCode}
                                                onChange={(e) => setCogsAccountCode(e.target.value)}
                                                disabled={syncing}
                                                dataCy="sync-invoices-override-account"
                                                items={accountsList.map(a => ({ value: a.code, key: `${a.code} - ${a.name}` }))}
                                            />
                                            <span className="d-block mt-1 small italic-subtext">
                                                Used for Supplier invoices (COGS/materials).
                                            </span>
                                        </>
                                    )}
                                </Col>
                                <Col md={6}>
                                    {entityType === 'bill' ? (
                                        <SelectInput
                                            label="Standard Expense Tax Type"
                                            value={expenseTaxType}
                                            onChange={(e) => setExpenseTaxType(e.target.value)}
                                            disabled={syncing}
                                            dataCy="sync-invoices-override-tax"
                                            items={taxRatesList.map(t => ({
                                                value: t.code || t.id || t.name,
                                                key: `${t.name} (${(t.rate !== undefined ? t.rate : t.effectiveRate) !== undefined ? `${t.rate !== undefined ? t.rate : t.effectiveRate}%` : 'Tax Rate'})`
                                            }))}
                                        />
                                    ) : (
                                        <SelectInput
                                            label="Standard Sales Tax Type"
                                            value={taxType}
                                            onChange={(e) => setTaxType(e.target.value)}
                                            disabled={syncing}
                                            dataCy="sync-invoices-override-tax"
                                            items={taxRatesList.map(t => ({
                                                value: t.code || t.id || t.name,
                                                key: `${t.name} (${(t.rate !== undefined ? t.rate : t.effectiveRate) !== undefined ? `${t.rate !== undefined ? t.rate : t.effectiveRate}%` : 'Tax Rate'})`
                                            }))}
                                        />
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group className="mb-0">
                                        <Form.Label className="small font-weight-bold text-uppercase mb-1">
                                            Exchange Rate
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.0001"
                                            value={exchangeRate}
                                            onChange={(e) => setExchangeRate(e.target.value)}
                                            disabled={syncing}
                                            placeholder="1.0"
                                            data-cy="sync-invoices-override-exchange-rate"
                                        />
                                    </Form.Group>
                                    <div className="mt-1 small">
                                        ℹ️ Used for multi-currency transactions. Defaults to 1.0.
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}
